import axios from "axios";
import { Component, toNative } from "vue-facing-decorator";
import type { SnotifyToast, SnotifyToastConfig } from "vue3-snotify";

import uniqueId from "lodash-es/uniqueId";
import { MiaPlaza } from "../../../../Reinforced.Typings";
import { type ForNotification } from "./DerivedInterfaces";
import { Notification } from "./Notification";

/*
 * A notification implemented through vue-snotify
 *
 * We use @Component on this base class so its mounted() hook is setup.
 * Note that this means that you cannot use super in derived components.
 */
@Component
export class SNotification<T extends ForNotification<MiaPlaza.Control.Vue.Notifications.Shared.INotification>>
	extends Notification<T> {
	toast: SnotifyToast | null = null;

	private uid: string = uniqueId();

	protected getDefaultToastConfig(): SnotifyToastConfig {
		return {
			...this.$snotify.config.toast,
			html: `<div id="${this.getPortalId()}" />`,
			buttons: [],
		};
	}

	getPortalId(button?: string): string {
		const id = `Notification_portal_${this.uid}`;
		if (button != null) {
			return `${id}_${button}`;
		}
		else {
			return id;
		}
	}

	protected getToastConfig(): SnotifyToastConfig {
		// We use @Component on this base class so its mounted() hook is setup.
		// Note that this means that you cannot use super in derived components.
		// Therefore, we can not call super.getToastConfig in derived classes.
		return this.getDefaultToastConfig();
	}

	protected getToastConstructor(): (config: SnotifyToastConfig) => SnotifyToast {
		return (config: SnotifyToastConfig) => this.$snotify.create({ config })!;
	}

	// Return a promise which resolves once a notification has been created and mounted.
	protected createToast(): Promise<SnotifyToast> {
		return new Promise((resolve) => {
			if (this.toast == null) {
				this.toast = this.getToastConstructor()(this.getToastConfig());
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.toast.on("mounted", () => resolve(this.toast!));
			}
			else {
				resolve(this.toast);
			}
		});
	}

	protected async dismissOnServer(): Promise<void> {
		if (this.from.DismissUrl != null) {
			await axios.get(this.from.DismissUrl);
		}
	}

	protected hide(): void {
		if (this.toast != null) {
			this.$snotify.remove(this.toast.id);
			this.toast = null;
		}
	}

	protected mounted(): void {
		void this.createToast();
	}

	protected beforeUnmount(): void {
		// Hide the notification when this component is unmounted, mostly so that we do not popup twice
		// if this gets somehow remounted.
		if (this.toast != null) {
			this.$snotify.remove(this.toast.id);
		}
	}
}

export default toNative(SNotification);
